import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { DataTable } from "@/components/DataTable";
import { WorkflowWithTriggersAndTags } from "@/generated";
import { timeAgo } from "@/utils/generic";
import { FlowTagsStack } from "../Tags/FlowTagsStack";
import { FlowTagActionsDialog, TagAction } from "../Tags/FlowTagActionsDialog";
import { TriggersStack } from "./TriggersStack";
import { CheckCheck, Plus, SearchX, Workflow } from "lucide-react";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { DATA_KEYS, prefetchQuery, useRemoteData } from "@/utils/useRemoteData";
import { ChipFilter } from "../UI/ChipFilter";
import { FlowTagsMenu } from "../Tags/FlowTagsMenu";
import { useSpotUser } from "@/utils/spotUser";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { OptionType } from "../Form/SelectField";
import { usePathname } from "next/navigation";
import { useFlowTableContextMenu } from "./useFlowTableContextMenu";
import { WorkflowEditModal } from "../Modals/WorkflowEditModal";
import { WorkflowVersionActionDialog } from "../Dialogs/WorkflowVersionActionDialog";
import { SearchBar } from "../SearchBar";
import LoadingPageWorkshop from "../Views/Workshop/LoadingPageWorkshop";
import WorkshopEmpty from "../Views/Workshop/WorkshopEmpty";
import { WorkflowIconPicker } from "./WorkflowIconPicker";
import { WorkflowDeleteDialog } from "../Modals/WorkflowDeleteDialog";
import { Badge } from "../shadcn/badge";
import { useDisclosure } from "@/utils/useDisclosure";
import { WORKSPACE_PERMISSIONS } from "@/constants/permissions";
import { usePermissions } from "@/hooks/usePermissions";

const SizeOptions = [
  { label: "15 per page", value: "15" },
  { label: "20 per page", value: "20" },
  { label: "25 per page", value: "25" },
  { label: "30 per page", value: "30" },
];

const StatusOptions = [
  {
    label: "All",
    value: "all",
  },
  {
    label: "Triggers",
    value: "triggers",
  },
  {
    label: "Live",
    value: "app_deployed",
  },
  {
    label: "Deleted",
    value: "deleted",
  },
];

export const FlowsTable = ({
  defaultPageSize,
  ignoreIds,
  readOnly,
}: {
  defaultPageSize?: number;
  ignoreIds?: string[];
  readOnly?: boolean;
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { spotGroup } = useSpotUser();
  // With Editor V2 as the workspace default, flag the legacy V1 flows; with V1
  // as the default, flag the newer V2 flows.
  const editorV2Enabled = Boolean(spotGroup.settings?.editor_v2_enabled);
  const { hasPermission } = usePermissions();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const editModalControls = useDisclosure();

  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagAction, setTagAction] = useState<TagAction>();
  const [selectedStatus, setSelectedStatus] = useState(StatusOptions[0]);
  const [selectedWorkflow, setSelectedWorkflow] =
    useState<WorkflowWithTriggersAndTags | null>(null);
  const [versionActionDialog, setVersionActionDialog] = useState<{
    isOpen: boolean;
    action: "duplicate" | "export";
    workflow: WorkflowWithTriggersAndTags | null;
  }>({ isOpen: false, action: "duplicate", workflow: null });

  // Default value
  const [pageSize, setPageSize] = useState(() => {
    if (defaultPageSize) {
      return defaultPageSize;
    }
    const height = window.innerHeight;
    if (height < 1050) {
      return Number(SizeOptions[0].value); // 15
    } else if (height < 1220) {
      return Number(SizeOptions[1].value); // 30
    } else {
      return Number(SizeOptions[2].value); // 45
    }
  });

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  /* Tags data */
  const { data: tagsData, isLoading: isTagsLoading } = useRemoteData(
    DATA_KEYS.TAGS,
    {
      path: { group_id: spotGroup.id },
    }
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.removeQueries({
      queryKey: [DATA_KEYS.WORKFLOWS_ID],
    });
  }, [queryClient]);

  /* Workflows data */
  const { data: workflowsData, isLoading } = useRemoteData(
    DATA_KEYS.WORKFLOWS,
    {
      path: { group_id: spotGroup.id },
      query: {
        page: currentPage,
        size: pageSize,
        query: searchValue || undefined,
        tags: selectedTags?.join(",") || undefined,
        status:
          selectedStatus.value === "all" ? undefined : selectedStatus.value,
        order_by: sortBy || undefined,
        sort: sortOrder || undefined,
        ignore_ids: ignoreIds?.join(",") || undefined,
      },
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  const onSort = (sorting: SortingState) => {
    if (sorting.length > 0) {
      setSortBy(sorting[0].id);
      setSortOrder(sorting[0].desc ? "desc" : "asc");
    } else {
      setSortBy(undefined);
      setSortOrder(undefined);
    }
  };

  /* Memo */
  const getNoDataMessage = useMemo(() => {
    return searchValue !== "" && searchValue != undefined
      ? "The search retrieved no results."
      : selectedStatus.value === "deleted"
        ? "There are no deleted flows."
        : selectedStatus.value === "triggers"
          ? "There are no flows with triggers."
          : selectedStatus.value === "app_deployed"
            ? "There are no app deployed flows."
            : "There are no flows to display.";
  }, [searchValue, selectedStatus.value]);

  const { getContextMenuItems } = useFlowTableContextMenu({
    selectedStatus,
    editModalControls,
    setDeleteModalOpen,
    workflow: selectedWorkflow,
    onDuplicate: (workflow) => {
      setVersionActionDialog({ isOpen: true, action: "duplicate", workflow });
    },
    onExport: (workflow) => {
      setVersionActionDialog({ isOpen: true, action: "export", workflow });
    },
  });

  /* Columns */
  const columns: ColumnDef<WorkflowWithTriggersAndTags, any>[] = useMemo(
    () => [
      {
        id: "name",
        accessorKey: "name",
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2">
              <div
                className="flex h-fit shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <WorkflowIconPicker
                  icon={row.original.meta?.icon || "workflow"}
                  flowId={row.original.id}
                  size="sm"
                />
              </div>
              <p className="text-foreground truncate min-w-0">
                {row.original.name}
              </p>
              {editorV2Enabled
                ? row.original.flow_version !== "v2" && (
                    <Badge variant="color-gray" isCompact>
                      V1
                    </Badge>
                  )
                : row.original.flow_version === "v2" && (
                    <Badge variant="color-violet" isCompact>
                      V2
                    </Badge>
                  )}
              {row.original.app_settings_v2?.is_active && (
                <Badge variant="outline">Live</Badge>
              )}
            </div>
          );
        },
        header: "Name",
        sortDescFirst: true,
        meta: {
          isSortable: true,
          wrap: false,
          width: "30%",
        },
      },
      {
        id: "tags",
        cell: ({ row }) => {
          return (
            <div
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <FlowTagsStack
                onEditTag={(tag) => setTagAction({ type: "edit", tag })}
                onDeleteTag={(tag) => setTagAction({ type: "delete", tag })}
                entityId={row.original.id}
                entityType="workflow"
                entityTags={row.original.tags || []}
                disabled={readOnly || selectedStatus.value === "deleted"}
              />
            </div>
          );
        },
        header: "Tags",
        meta: {
          isSortable: false,
          width: "15%",
          noEllipsis: true,
        },
      },
      {
        id: "triggers",
        accessorKey: "triggers",
        cell: ({ row }) => <TriggersStack triggers={row.original.triggers} />,
        header: "Triggers",
        meta: {
          isSortable: false,
          width: "15%",
          noEllipsis: true,
        },
      },
      {
        id: "last_updated_at",
        accessorKey: "last_updated_at",
        cell: ({ row }) => {
          return row.original.last_updated_at
            ? timeAgo(row.original.last_updated_at)
            : "-";
        },
        header: "Last modified",
        sortDescFirst: true,
        meta: {
          isSortable: true,
          wrap: false,
          width: "15%",
        },
      },
    ],
    [selectedStatus.value, editorV2Enabled, readOnly]
  );

  const getRowHref = (workflow: WorkflowWithTriggersAndTags) => {
    return `/flows/${workflow.id}`;
  };

  const handlePageSizeChange = (value: OptionType) => {
    setPageSize(Number(value.value));
  };

  const handleContextMenuOpen = (workflow: WorkflowWithTriggersAndTags) => {
    setSelectedWorkflow(workflow);
  };

  const isLoadingPage = isLoading || isTagsLoading;
  const hasSearch = searchValue !== "";
  const hasOtherFilters =
    selectedStatus.value !== "all" || selectedTags.length > 0;
  const hasActiveFilters = hasSearch || hasOtherFilters;

  const hasNone = workflowsData?.total === 0 && !hasActiveFilters;
  const allIgnored = hasNone && Boolean(ignoreIds?.length);
  const isEmpty = hasNone && !allIgnored;

  const clearFilters = () => {
    setSearchValue("");
    setSelectedStatus(StatusOptions[0]);
    setSelectedTags([]);
    setCurrentPage(1);
  };

  const noMatchDescription = hasSearch
    ? hasOtherFilters
      ? "Try a different search term or adjust your filters."
      : "Try a different search term."
    : "Try adjusting your filters.";

  return (
    <>
      {/* Filters */}
      <div className="w-full mb-4">
        <div className="flex justify-between gap-2 items-center">
          <SearchBar
            placeholder="Search in this workspace"
            isDebounced
            value={searchValue}
            setValue={handleSearchValueChange}
          />
          <div className="flex gap-2 items-center">
            <ChipFilter
              selectedOrder={selectedStatus}
              orderSelectOptions={StatusOptions}
              setSelectedOrder={setSelectedStatus}
              labelIfFirstOption="Status"
              side="bottom"
              align="start"
            />
            <FlowTagsMenu
              tags={tagsData || []}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
            />
          </div>
        </div>
      </div>

      {isLoadingPage && (
        <LoadingPageWorkshop pageSize={pageSize} columns={columns} />
      )}

      {!isLoadingPage && (
        <DataTable
          columns={columns}
          data={workflowsData?.items || []}
          isLoading={isLoading}
          loadingRows={pageSize}
          rowHref={getRowHref}
          noDataMessage={getNoDataMessage}
          onSort={onSort}
          contextMenuItems={readOnly ? undefined : getContextMenuItems}
          onContextMenuOpen={readOnly ? undefined : handleContextMenuOpen}
          noPrefetchLinks
          tableControls={{
            data: workflowsData,
            currentPage,
            setCurrentPage,
            pageSize,
            onPageSizeChange: handlePageSizeChange,
          }}
          emptyState={
            hasActiveFilters ? (
              <NoxusEmptyBlock
                size="sm"
                icon={SearchX}
                title={
                  hasSearch
                    ? "No flows match your search"
                    : "No flows match your filters"
                }
                description={noMatchDescription}
                button={
                  hasSearch && !hasOtherFilters
                    ? "Clear search"
                    : "Clear filters"
                }
                buttonVariant="outline"
                onClick={clearFilters}
              />
            ) : allIgnored ? (
              <NoxusEmptyBlock
                size="sm"
                icon={CheckCheck}
                title="All Flows are already open"
                description="Every Flow in this workspace is already open in a tab."
              />
            ) : isEmpty ? (
              <WorkshopEmpty
                toolId="agentic-workflow"
                icon={Workflow}
                title="No Flows created yet"
                readerDescription="There are currently no Flows available for this group."
                button="Create Flow"
                buttonIcon={Plus}
                size="sm"
                description="Automate multi-step tasks with AI-powered workflows that handle decisions, data processing, and actions."
              />
            ) : undefined
          }
        />
      )}

      {selectedWorkflow && editModalControls.isOpen && (
        <WorkflowEditModal
          isOpen={editModalControls.isOpen}
          onClose={editModalControls.onClose}
          workflow={selectedWorkflow}
        />
      )}
      {selectedWorkflow && deleteModalOpen && (
        <WorkflowDeleteDialog
          isOpen={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          flowId={selectedWorkflow.id}
          flowName={selectedWorkflow.name}
          flowIcon={selectedWorkflow.meta?.icon ?? undefined}
          isAppPublished={selectedWorkflow.app_settings_v2?.is_active || false}
        />
      )}
      {versionActionDialog.workflow && versionActionDialog.isOpen && (
        <WorkflowVersionActionDialog
          isOpen={versionActionDialog.isOpen}
          onOpenChange={(isOpen) =>
            setVersionActionDialog({ ...versionActionDialog, isOpen })
          }
          workflowId={versionActionDialog.workflow.id}
          workflowName={versionActionDialog.workflow.name}
          action={versionActionDialog.action}
        />
      )}
      <FlowTagActionsDialog
        action={tagAction}
        setAction={setTagAction}
        onDeleted={(tagId) => {
          setSelectedTags((current) => current.filter((id) => id !== tagId));
        }}
      />
    </>
  );
};
